import { useEffect, useState } from 'react'
import { type NavigateFunction, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { NoQuery } from '#/view/NoQuery'

import { TabNavigation } from '#/local/component/TabNavigation'
import { Title } from '#/local/component/Title'

// import { NoResult } from '#/view/NoResult'
import { fetchItemsCount } from '#/local/repository/Main'

export type Engine = 'productsEshop' | 'productsRental' | 'categoriesEshop' | 'categoriesRental' | 'articles' | 'productsSeason'
export type ItemsCount = Record<Engine | 'totalCount', number>

export const App = () => {
    const [searchCount, setSearchCount] = useState<ItemsCount>()
    const [searchParameters] = useSearchParams()
    const { branchTitle } = useParams()
    const navigate = useNavigate()

    const { pathname } = useLocation()

    const query = searchParameters.get('query')

    useEffect(() => {
        if (!query) {
            return
        }
        void fetchItemsCount(query)
            .then(data => createViewList(data))
            .then(data => data !== false && setSearchCount(data))
    }, [query])

    if (!query) {
        return <NoQuery />
    }

    if (searchCount === undefined) {
        return null
    }

    if (searchCount.totalCount === 0) {
        void navigate(`${branchTitle ? `/${branchTitle}` : ''}/search/no-result?query=${query}`)
        return
    }

    if (!hasCurrentViewItems(searchCount, pathname)) {
        setViewByItemsCount(searchCount, branchTitle ?? '', query, navigate)
    }

    return (
        <>
            <Title />
            <TabNavigation views={searchCount} />
            <Outlet />
        </>
    )
}

const createViewList = (data: { engine_name: Engine, total_count: number }[] | { redirect_url: string }) => {
    const itemsCountMapper: Record<string, Engine> = {
        'products-eshop': 'productsEshop',
        'products-rental': 'productsRental',
        'articles': 'articles',
        'nodes-eshop': 'categoriesEshop',
        'nodes-rental': 'categoriesRental',
        // 'products-bonus': 'productsSeason',
    }

    const list: ItemsCount = {
        articles: 0,
        categoriesEshop: 0,
        categoriesRental: 0,
        productsEshop: 0,
        productsRental: 0,
        productsSeason: 0,
        totalCount: 0,
    }

    if ('redirect_url' in data) {
        location.href = data.redirect_url
        return false
    }

    // vytvoří objekt, který má počet záznamů pro každý engine v `itemsCountMapper`
    for (const item of data) {
        const engineName = itemsCountMapper[item.engine_name]

        // pokud neexistuje v nameMapList, tak to ignoruje
        if (!engineName) {
            continue
        }

        list[engineName] = item.total_count
        list.totalCount += item.total_count
    }

    return list
}

const hasCurrentViewItems = (count: ItemsCount, pathname: string) => {
    if (pathname.endsWith('/search')) {
        return false
    }

    if (pathname.endsWith('/eshop') && count.productsEshop === 0) {
        return false
    }

    if (pathname.endsWith('/rental') && count.productsRental === 0) {
        return false
    }

    if (pathname.endsWith('/article') && count.articles === 0) {
        return false
    }

    if (pathname.endsWith('/category') && count.categoriesEshop === 0 && count.categoriesRental === 0) {
        return false
    }

    if (pathname.endsWith('/season') && count.productsSeason === 0) {
        return false
    }

    return true
}

const setViewByItemsCount = (count: ItemsCount, branchTitle: string, query: string, navigate: NavigateFunction) => {
    branchTitle = branchTitle ? `/${branchTitle}` : ''

    if (count.productsEshop > 0) {
        void navigate(`${branchTitle}/search/eshop?query=${query}`)
        return
    }
    if (count.productsRental > 0) {
        void navigate(`${branchTitle}/search/rental?query=${query}`)
        return
    }
    if (count.articles > 0) {
        void navigate(`${branchTitle}/search/article?query=${query}`)
        return
    }
    if (count.categoriesEshop > 0 || count.categoriesRental > 0) {
        void navigate(`${branchTitle}/search/category?query=${query}`)
        return
    }
    if (count.productsSeason > 0) {
        void navigate(`${branchTitle}/search/season?query=${query}`)
        return
    }
    void navigate(`${branchTitle}/search/no-result?query=${query}`)
}
